<template>
  <v-container class="py-6" fluid>
    <div class="d-flex align-center mb-6">
      <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
        <v-icon>mdi-account-group</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h5 font-weight-bold primary--text mb-0">
          {{ $t('extracurricular_activity_students') }} (Drag & Drop)
        </h1>
        <span class="text-caption grey--text font-weight-medium">
          Inscribe estudiantes arrastrándolos a la actividad deseada
        </span>
      </div>
    </div>

    <v-row>
      <!-- LEFT SECTION: STUDENTS BY CLASSROOM -->
      <v-col cols="12" md="6">
        <v-card class="elevation-12 rounded-xl overflow-hidden glass-card fill-height">
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="primary">mdi-google-classroom</v-icon>
            <v-toolbar-title class="font-weight-bold primary--text">Estudiantes</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-autocomplete
              v-model="selectedClassroomId"
              :items="classrooms"
              :item-text="classroomText"
              item-value="id"
              :label="$t('classrooms')"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              class="rounded-lg bg-grey-lighten-4 mb-4"
              @change="filterStudents"
            ></v-autocomplete>

            <v-text-field
              v-model="searchStudent"
              append-icon="mdi-account-search"
              :label="$t('Buscar')"
              single-line
              hide-details
              outlined
              dense
              class="rounded-lg bg-grey-lighten-4 mb-6"
            ></v-text-field>

            <v-divider class="mb-4"></v-divider>

            <div class="list-container custom-scrollbar">
              <v-progress-linear v-if="loadingStudents" indeterminate color="primary"></v-progress-linear>
              
              <v-row v-if="filteredStudents.length > 0">
                <v-col 
                  v-for="student in filteredStudents" 
                  :key="student.id" 
                  cols="12"
                  class="py-1"
                >
                  <div 
                    class="student-card d-flex align-center pa-3 rounded-lg border shadow-xs"
                    draggable="true"
                    @dragstart="onDragStart($event, student)"
                  >
                    <v-avatar size="40" class="mr-3 elevation-1">
                      <v-img :src="student.avatar || defaultAvatar"></v-img>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2">{{ student.first_name }} {{ student.last_name }}</div>
                      <div class="text-caption grey--text">{{ getStudentClassroomName(student.idClassroom) }}</div>
                    </div>
                    <v-icon color="grey lighten-1">mdi-drag-variant</v-icon>
                  </div>
                </v-col>
              </v-row>
              
              <div v-else-if="!loadingStudents" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-account-search-outline</v-icon>
                <p class="grey--text mt-4">Selecciona un aula para ver estudiantes</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT SECTION: ACTIVITY ENROLLMENT -->
      <v-col cols="12" md="6">
        <v-card 
          class="elevation-12 rounded-xl overflow-hidden glass-card fill-height"
          :class="{'drop-zone-active': isDraggingOver}"
          @dragover.prevent="isDraggingOver = true"
          @dragleave="isDraggingOver = false"
          @drop="onDrop"
        >
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="secondary">mdi-star-circle</v-icon>
            <v-toolbar-title class="font-weight-bold secondary--text">Actividad Extracurricular</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-autocomplete
              v-model="selectedActivityId"
              :items="activities"
              item-text="name"
              item-value="idExtracurricularActivity"
              :label="$t('placeholder_choose_extracurricular_activity')"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-star"
              class="rounded-lg bg-grey-lighten-4 mb-6"
              @change="getEnrollmentsByActivity"
            >
              <template v-slot:item="data">
                <v-list-item-icon>
                  <v-icon :color="getActivityColor(data.item.idExtracurricularActivity)">
                    {{ getActivityIcon(data.item.idExtracurricularActivity) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getBaseActivityName(data.item.idActivity) }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-divider class="mb-4"></v-divider>

            <div 
              class="drop-area custom-scrollbar"
              :class="{'has-activity': !!selectedActivityId}"
            >
              <div v-if="!selectedActivityId" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-star-outline</v-icon>
                <p class="grey--text mt-4">Selecciona una actividad para inscribir estudiantes</p>
              </div>

              <div v-else>
                <div class="d-flex align-center mb-4">
                  <v-chip 
                    :color="getActivityColor(selectedActivityId)" 
                    dark 
                    class="font-weight-bold"
                  >
                    <v-icon left>{{ getActivityIcon(selectedActivityId) }}</v-icon>
                    {{ getActivityName(selectedActivityId) }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-bold grey--text">
                    {{ enrollmentList.length }} ESTUDIANTES INSCRITOS
                  </span>
                </div>

                <v-progress-linear v-if="loadingEnrollments" indeterminate color="secondary" class="mb-4"></v-progress-linear>

                <div v-if="enrollmentList.length > 0">
                  <v-row>
                    <v-col 
                      v-for="enrollment in enrollmentList" 
                      :key="enrollment.idStudentExtracurricularActivity" 
                      cols="12"
                      class="py-1"
                    >
                      <v-hover v-slot="{ hover }">
                        <div class="enrolled-card d-flex align-center pa-3 rounded-lg border shadow-xs transition-all" :class="{'elevation-4': hover}">
                          <v-avatar size="40" class="mr-3 elevation-1">
                            <v-img :src="getStudentAvatar(enrollment.idStudent) || defaultAvatar"></v-img>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-subtitle-2">{{ getStudentName(enrollment.idStudent) }}</div>
                            <div class="text-caption grey--text">{{ formatDate(enrollment.registrationDate) }}</div>
                          </div>
                          <v-btn 
                            icon 
                            small 
                            color="error" 
                            class="opacity-0 transition-all" 
                            :class="{'opacity-100': hover}"
                            @click="confirmDelete(enrollment)"
                          >
                            <v-icon small>mdi-account-minus</v-icon>
                          </v-btn>
                        </div>
                      </v-hover>
                    </v-col>
                  </v-row>
                </div>
                
                <div v-else-if="!loadingEnrollments" class="text-center py-12 drop-instruction border-dashed rounded-xl border-2">
                  <v-icon size="48" color="primary" class="animate-bounce">mdi-tray-arrow-down</v-icon>
                  <p class="primary--text font-weight-bold mt-4">Arrastra estudiantes aquí para inscribirlos</p>
                  <span class="text-caption grey--text">Solo suelta un estudiante para completar el proceso</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px" transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden glass-card p-4">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="error" large class="mr-3">mdi-account-remove-outline</v-icon>
          <span class="font-weight-bold">{{ $t('aviso') }}</span>
        </v-card-title>

        <v-card-text class="pt-4 text-body-1 dark--text font-weight-medium">
          {{ $t('msg_delete_activity_student') }}
          <div v-if="itemToDelete" class="mt-4 pa-4 bg-grey-lighten-4 rounded-lg border-l-4-error shadow-inner">
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-account-child</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Estudiante</span>
            </div>
            <div class="font-weight-bold primary--text mb-3">
              {{ getStudentName(itemToDelete.idStudent) }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6" @click="dialogDelete = false">No</v-btn>
          <v-btn
            color="error"
            class="rounded-pill px-8 elevation-4 shadow-error hover-scale transition-all"
            @click="deleteItemConfirm"
            :loading="deleting"
          >
            {{ $t('Si') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'
import { mapGetters } from 'vuex'

export default {
  name: 'ExtracurricularActivityStudentsDragDrop',
  data: () => ({
    searchStudent: '',
    loadingData: false,
    loadingStudents: false,
    loadingEnrollments: false,
    loadingActivities: false,
    loadingClassrooms: false,
    deleting: false,
    
    selectedClassroomId: null,
    selectedActivityId: null,
    isDraggingOver: false,

    allStudents: [],
    activities: [],
    classrooms: [],
    baseActivities: [],
    enrollmentList: [],
    
    dialogDelete: false,
    itemToDelete: null,
    
    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    ...mapGetters(['getProfile']),
    filteredStudents() {
      let filtered = this.allStudents;
      
      if (this.selectedClassroomId) {
        filtered = filtered.filter(s => s.idClassroom === this.selectedClassroomId);
      } else {
        // If no classroom selected, maybe we shouldn't show students to encourage filtering
        return [];
      }
      
      if (this.searchStudent) {
        const query = this.searchStudent.toLowerCase();
        filtered = filtered.filter(s => 
          s.first_name.toLowerCase().includes(query) || 
          s.last_name.toLowerCase().includes(query)
        );
      }
      
      // Also filter out students already enrolled in the selected activity
      if (this.selectedActivityId) {
        filtered = filtered.filter(s => 
          !this.enrollmentList.some(e => e.idStudent === s.id)
        );
      }
      
      return filtered;
    }
  },

  created() {
    this.init();
  },

  methods: {
    async init() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.getStudents(),
          this.getActivities(),
          this.getBaseActivities(),
          this.getClassrooms()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        this.loadingData = false;
      }
    },

    getStudents() {
      this.loadingStudents = true;
      return HTTP.get('student/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.allStudents = res.data;
      }).finally(() => { this.loadingStudents = false; });
    },

    getActivities() {
      this.loadingActivities = true;
      return HTTP.get('extracurricular_activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.activities = res.data;
      }).finally(() => { this.loadingActivities = false; });
    },

    getBaseActivities() {
      return HTTP.get('activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.baseActivities = res.data;
      });
    },

    getClassrooms() {
      this.loadingClassrooms = true;
      return HTTP.get('classroom/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.classrooms = res.data;
      }).finally(() => { this.loadingClassrooms = false; });
    },

    getEnrollmentsByActivity() {
      if (!this.selectedActivityId) {
        this.enrollmentList = [];
        return;
      }
      this.loadingEnrollments = true;
      const url = `student_extracurricular_activity_by_activity/${this.selectedActivityId}/`;

      return HTTP.get(url, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.enrollmentList = res.data;
      }).catch(err => {
        console.error('Error fetching enrollments:', err);
      }).finally(() => { this.loadingEnrollments = false; });
    },

    classroomText(item) {
      return `${item.grade_name || ''} ${item.name || ''}`.trim() || 'Desconocido';
    },

    getStudentClassroomName(idClassroom) {
      const classroom = this.classrooms.find(c => c.id === idClassroom);
      return classroom ? this.classroomText(classroom) : 'N/A';
    },

    getStudentName(idStudent) {
      const student = this.allStudents.find(s => s.id === idStudent);
      return student ? `${student.first_name} ${student.last_name}` : 'Cargando...';
    },

    getStudentAvatar(idStudent) {
      const student = this.allStudents.find(s => s.id === idStudent);
      return student ? student.avatar : null;
    },

    getActivityName(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      return activity ? activity.name : '';
    },

    getBaseActivityName(idBaseActivity) {
      const base = this.baseActivities.find(b => b.idActivity === idBaseActivity);
      return base ? base.name : '';
    },

    getActivityIcon(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      if (!activity) return 'mdi-star';
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity);
      return base && base.workshop ? 'mdi-palette' : 'mdi-run-fast';
    },

    getActivityColor(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      if (!activity) return 'grey';
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity);
      return base && base.workshop ? 'success' : 'info';
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString();
    },

    // DRAG AND DROP HANDLERS
    onDragStart(event, student) {
      event.dataTransfer.setData('studentId', student.id);
      event.dataTransfer.effectAllowed = 'copy';
      // Store current activity id to ensure we drop on the right context
    },

    onDrop(event) {
      this.isDraggingOver = false;
      const studentId = event.dataTransfer.getData('studentId');
      
      if (!studentId) return;
      if (!this.selectedActivityId) {
        this.showSnackbar('Por favor selecciona una actividad primero', 'warning');
        return;
      }

      this.enrollStudent(studentId, this.selectedActivityId);
    },

    enrollStudent(idStudent, idExtracurricularActivity) {
      // Check if already enrolled (should be filtered out, but double check)
      if (this.enrollmentList.some(e => e.idStudent === idStudent)) {
        this.showSnackbar(this.$t('msg_duplicate_student_assignment'), 'warning');
        return;
      }

      this.loadingEnrollments = true;
      HTTP.post('student_extracurricular_activity/', {
        idStudent,
        idExtracurricularActivity
      }, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.$t('msg_student_activity_added'), 'success');
        this.getEnrollmentsByActivity();
      }).catch(err => {
        console.error('Error enrolling student:', err);
        this.showSnackbar('Error al inscribir al estudiante', 'error');
        this.loadingEnrollments = false;
      });
    },

    confirmDelete(enrollment) {
      this.itemToDelete = enrollment;
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.deleting = true;
      const id = this.itemToDelete.idStudentExtracurricularActivity;
      
      HTTP.delete(`student_extracurricular_activity/${id}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.$t('msg_student_activity_deleted'), 'success');
        this.getEnrollmentsByActivity();
        this.dialogDelete = false;
      }).catch(err => {
        console.error('Error deleting enrollment:', err);
        this.showSnackbar('Error al dar de baja', 'error');
      }).finally(() => { this.deleting = false; });
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.border-2-white {
  border: 2px solid white;
}

.shadow-xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.list-container, .drop-area {
  height: 500px;
  overflow-y: auto;
  padding: 4px;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.student-card {
  border: 1px solid #edf2f7;
  background-color: white;
  transition: all 0.2s ease;
  cursor: grab;
}
.student-card:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
  transform: translateX(4px);
}
.student-card:active {
  cursor: grabbing;
}

.enrolled-card {
  border: 1px solid #edf2f7;
  background-color: white;
}

.drop-zone-active {
  background-color: rgba(var(--v-primary-base), 0.05) !important;
  border: 2px dashed var(--v-primary-base) !important;
}

.drop-instruction {
  border-color: #e2e8f0;
  background-color: #f8fafc;
}

.border-dashed {
  border-style: dashed !important;
}

.border-2 {
  border-width: 2px !important;
}

.opacity-0 { opacity: 0; }
.opacity-100 { opacity: 1; }

.animate-bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-20px);}
  60% {transform: translateY(-10px);}
}

.hover-scale:hover {
  transform: translateY(-2px);
}

.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.3) !important;
}

.border-l-4-error {
  border-left: 4px solid #ff5252;
}

.tracking-wider {
  letter-spacing: 0.5px;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}
</style>
